/**
 * Resilient 401 recovery for cookie-based JWT sessions.
 * Handles refresh-token rotation races (e.g. another tab already refreshed).
 */

export interface Resilient401Options {
  fullUrl: string;
  retryRequest: () => Promise<Response>;
}

export interface Resilient401Result {
  recovered: boolean;
  response?: Response;
  abandonSession: boolean;
}

const COOKIE_SYNC_DELAY_MS = 100;

/** Paths allowed without auth; do not redirect to login when user is on these. */
export const PUBLIC_AUTH_PATHS = [
  '/',
  '/login',
  '/register',
  '/verify',
  '/forgot-password',
  '/reset-password',
];

export function isStreamApiUrl(url: string): boolean {
  return url.includes('/api/stream/');
}

/**
 * Attempt token refresh, then retry the original request.
 * If refresh fails definitively, wait briefly and retry with existing cookies
 * (another tab may have already rotated tokens).
 */
export async function handleUnauthorizedWithResilientRefresh(
  options: Resilient401Options
): Promise<Resilient401Result> {
  const { retryRequest } = options;

  try {
    const { authManager, isTransientTokenRefreshError } = await import('./authManager');

    if (!authManager.hasSession()) {
      return { recovered: false, abandonSession: true };
    }

    try {
      const refreshSuccess = await authManager.refreshToken();
      if (refreshSuccess) {
        const retryResponse = await retryRequest();
        if (retryResponse.ok) {
          return { recovered: true, response: retryResponse, abandonSession: false };
        }
        return { recovered: false, response: retryResponse, abandonSession: false };
      }
    } catch (refreshError) {
      if (isTransientTokenRefreshError(refreshError)) {
        return { recovered: false, abandonSession: false };
      }

      const cookieRetry = await retryWithExistingCookies(retryRequest);
      if (cookieRetry.recovered) {
        return cookieRetry;
      }
      return { recovered: false, abandonSession: true };
    }

    const cookieRetry = await retryWithExistingCookies(retryRequest);
    if (cookieRetry.recovered) {
      return cookieRetry;
    }
    return { recovered: false, abandonSession: true };
  } catch {
    return { recovered: false, abandonSession: true };
  }
}

async function retryWithExistingCookies(
  retryRequest: () => Promise<Response>
): Promise<Resilient401Result> {
  await new Promise((resolve) => setTimeout(resolve, COOKIE_SYNC_DELAY_MS));
  try {
    const finalRetry = await retryRequest();
    if (finalRetry.ok) {
      return { recovered: true, response: finalRetry, abandonSession: false };
    }
    if (finalRetry.status !== 401 && finalRetry.status !== 403) {
      return { recovered: false, response: finalRetry, abandonSession: false };
    }
  } catch {
    return { recovered: false, abandonSession: false };
  }
  return { recovered: false, abandonSession: true };
}

/**
 * Clear client session and redirect to login when appropriate.
 */
export async function abandonUserSession(fullUrl: string): Promise<void> {
  const { authManager } = await import('./authManager');
  const { setIsLoggedIn, setUser, setSubscription } = await import('../store/userStore');

  authManager.clearTokens();
  setIsLoggedIn(false);
  setUser(null);
  setSubscription(null);

  if (typeof window === 'undefined' || isStreamApiUrl(fullUrl)) {
    return;
  }

  const currentPath = window.location.pathname;
  if (!PUBLIC_AUTH_PATHS.includes(currentPath)) {
    window.location.href = '/login';
  }
}
